<template>
  <component
    :is="chartComponent"
    v-if="chartData.datasets.length > 0"
    id="vokalis-chart-id"
    :options="chartOptions"
    :data="chartData"
    class="vokalis-chart"
  />

  <div v-else class="vokalis-chart__no-data">
    <div class="vokalis-chart__no-data-line"></div>
    <div class="vokalis-chart__no-data-line"></div>
    <div class="vokalis-chart__no-data-line"></div>
  </div>
</template>

<script>
import { Bar, Pie } from 'vue-chartjs'
import {
  Chart as ChartJS,
  ArcElement,
  LineElement,
  BarElement,
  PointElement,
  CategoryScale,
  LinearScale,
  DoughnutController,
  PieController,
  BarController,
  LineController,
  Tooltip,
  Legend,
} from 'chart.js'
import { getBaseColors } from '@baserow/modules/core/utils/colors'

ChartJS.register(
  ArcElement,
  LineElement,
  BarElement,
  PointElement,
  CategoryScale,
  LinearScale,
  DoughnutController,
  PieController,
  BarController,
  LineController,
  Tooltip,
  Legend
)

export default {
  name: 'VokalisChart',
  components: { Bar, Pie },
  props: {
    dataSource: { type: Object, required: true },
    dataSourceData: { type: Object, required: true },
    seriesConfig: { type: Array, required: true },
  },
  computed: {
    chartComponent() {
      const firstSeriesConfig =
        this.getIndividualSeriesConfig(this.chartSeries[0]?.id) || {}
      const chartType = this.convertChartJsType(
        firstSeriesConfig.series_chart_type
      )
      if (['pie', 'doughnut'].includes(chartType)) {
        return 'Pie'
      }
      return 'Bar'
    },
    result() {
      return this.dataSourceData?.result
    },
    chartSeries() {
      return (this.dataSource?.aggregation_series || []).map((s) => ({
        id: s.id,
        fieldId: s.field_id,
        aggregationType: s.aggregation_type,
      }))
    },
    primaryFieldId() {
      const properties = this.dataSource?.schema?.properties || {}
      const prop = Object.values(properties).find(
        (item) => item.metadata?.primary === true
      )
      return prop?.metadata?.id
    },
    chartOptions() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            display: true,
            align: 'start',
            position: 'bottom',
          },
        },
      }
    },
    chartData() {
      if (!this.result) {
        return { labels: [], datasets: [] }
      }
      if ((this.dataSource?.aggregation_group_bys || []).length > 0) {
        return this.chartDataGrouping
      }
      return this.chartDataNoGrouping
    },
    chartDataGrouping() {
      const groupByFieldId =
        this.dataSource?.aggregation_group_bys?.[0]?.field_id ??
        this.primaryFieldId

      const groupKey = `field_${groupByFieldId}`
      const labels = (this.result || []).map((item) =>
        this.getGroupLabel(item[groupKey])
      )

      const datasets = []
      for (const [index, series] of this.chartSeries.entries()) {
        const seriesKey = `field_${series.fieldId}_${series.aggregationType}`
        const seriesData = (this.result || []).map((item) => item[seriesKey])
        const seriesConfig = this.getIndividualSeriesConfig(series.id)
        const datasetType = this.convertChartJsType(
          seriesConfig.series_chart_type
        )
        const label = this.getSeriesLabel(series.fieldId, series.aggregationType)

        datasets.push({
          type: datasetType || 'bar',
          data: seriesData,
          label,
          ...this.getDatasetColors(datasetType || 'bar', index, seriesData.length),
        })
      }

      return { labels, datasets }
    },
    chartDataNoGrouping() {
      const labels = ['']
      const datasets = []
      for (const [index, series] of this.chartSeries.entries()) {
        const seriesKey = `field_${series.fieldId}_${series.aggregationType}`
        const value = this.result[seriesKey]
        const seriesConfig = this.getIndividualSeriesConfig(series.id)
        const datasetType = this.convertChartJsType(
          seriesConfig.series_chart_type
        )
        const label = this.getSeriesLabel(series.fieldId, series.aggregationType)

        datasets.push({
          type: datasetType || 'bar',
          data: [value],
          label,
          ...this.getDatasetColors(datasetType || 'bar', index, 1),
        })
      }
      return { labels, datasets }
    },
    seriesColors() {
      return getBaseColors().map((color) => {
        return {
          backgroundColor: color,
          borderColor: color,
          hoverBackgroundColor: color,
        }
      })
    },
    segmentPalette() {
      return [
        '#1D4ED8', // blue
        '#16A34A', // green
        '#DC2626', // red
        '#EA580C', // orange
        '#CA8A04', // yellow
        '#7C3AED', // violet
        '#0891B2', // cyan
        '#DB2777', // pink
        '#4F46E5', // indigo
        '#65A30D', // lime
        '#0F766E', // teal
        '#B91C1C', // crimson
        '#A16207', // amber
        '#4338CA', // deep indigo
        '#0EA5E9', // sky
        '#15803D', // emerald
      ]
    },
  },
  methods: {
    convertChartJsType(chartType) {
      if (!chartType) return null
      return String(chartType).toLowerCase()
    },
    getIndividualSeriesConfig(seriesId) {
      return (
        (this.seriesConfig || []).find((item) => item.series_id === seriesId) ||
        {}
      )
    },
    getSeriesLabel(fieldId, aggregationType) {
      const properties = this.dataSource?.schema?.properties || {}
      const fieldKey = `field_${fieldId}`
      const fieldTitle = properties[fieldKey]?.title || `Field ${fieldId}`
      const aggregationLabel = this.getAggregationTypeLabel(aggregationType)
      return `${fieldTitle} (${aggregationLabel})`
    },
    getAggregationTypeLabel(aggregationType) {
      const aggregation = this.$registry.get('viewAggregation', aggregationType)
      return aggregation?.getName?.() || aggregationType
    },
    getGroupLabel(rawValue) {
      if (rawValue === 'OTHER_VALUES') {
        return 'Others'
      }
      if (rawValue === null || rawValue === undefined || rawValue === '') {
        return ''
      }
      const groupByFieldId =
        this.dataSource?.aggregation_group_bys?.[0]?.field_id ??
        this.primaryFieldId
      const serializedField =
        this.dataSource?.context_data?.fields?.[`field_${groupByFieldId}`]

      if (serializedField?.type === 'single_select') {
        const option = serializedField.select_options?.find(
          (item) => String(item.id) === String(rawValue)
        )
        if (option?.value) {
          return option.value
        }
      }

      if (rawValue === true) {
        return 'True'
      }
      if (rawValue === false) {
        return 'False'
      }
      return String(rawValue)
    },
    getDatasetColors(datasetType, seriesIndex, valuesCount) {
      if (!['pie', 'doughnut'].includes(datasetType)) {
        return this.seriesColors[seriesIndex] || this.seriesColors[0]
      }
      const palette = this.segmentPalette
      const shifted = palette.map(
        (_color, i) => palette[(i + (seriesIndex % palette.length)) % palette.length]
      )
      const segmentColors = Array.from({ length: valuesCount }, (_v, i) => {
        return shifted[i % shifted.length]
      })
      return {
        backgroundColor: segmentColors,
        borderColor: '#FFFFFF',
        borderWidth: 1,
        hoverBackgroundColor: segmentColors,
      }
    },
  },
}
</script>

<style scoped>
.vokalis-chart {
  position: relative;
  width: 100%;
  height: 320px;
}
.vokalis-chart__no-data {
  height: 320px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 18px;
}
.vokalis-chart__no-data-line {
  height: 10px;
  border-radius: 6px;
  background: rgba(255, 255, 255, 0.08);
  border: 1px dashed rgba(255, 255, 255, 0.16);
}
</style>

